import { FC, useState } from 'react';
import { useUnmount } from 'usehooks-ts';

import { ContinuousLoadingFields } from './components/ContinuousLoadingFields';
import { SidebarWrap } from './SidebarWrap';
import { deselectNodesEdges } from '../../features/graph/graphSlice';
import { selectCommentsById } from '../../features/workflow/selectors';
import {
  setComment,
  updateTaskProperties,
} from '../../features/workflow/workflowSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
  AdditionalDataInput,
  type ContinuousLoadingProperties,
  PropertiesType,
} from '../../types';

interface ContinuousLoadingSidebarProps {
  selectedNodeId: string;
  properties: ContinuousLoadingProperties;
}

export const ContinuousLoadingSidebar: FC<ContinuousLoadingSidebarProps> = ({
  selectedNodeId,
  properties,
}) => {
  const dispatch = useAppDispatch();
  const comment = useAppSelector(selectCommentsById)[selectedNodeId];
  const [nickname, setNickname] = useState(properties.nickname);
  const [additionalDataInputs, setAdditionalDataInputs] = useState(
    properties.additionalDataInputs || []
  );
  const [isAdditionalDataDirty, setIsAdditionalDataDirty] = useState(false);

  const handleCloseSidebar = () => {
    dispatch(deselectNodesEdges());
  };

  const handleDataFieldsChange = (fields: AdditionalDataInput[]) => {
    setAdditionalDataInputs(fields);
    setIsAdditionalDataDirty(true);
    handleSubmit(fields);
  };

  const handleSetComment = (comment: string) =>
    dispatch(setComment({ id: selectedNodeId, comment }));

  const handleSubmit = (updatedFields?: AdditionalDataInput[]) => {
    const updates: Partial<ContinuousLoadingProperties> = {};
    let hasChanges = false;

    if (nickname !== properties.nickname) {
      updates.nickname = nickname;
      hasChanges = true;
    }

    if (updatedFields || isAdditionalDataDirty) {
      updates.additionalDataInputs = updatedFields || additionalDataInputs;
      hasChanges = true;
    }

    if (hasChanges) {
      dispatch(
        updateTaskProperties({
          id: selectedNodeId,
          nickname,
          ...updates,
          propertiesType: PropertiesType.ContinuousLoading,
        })
      );
    }
  };

  useUnmount(() => handleSubmit());

  return (
    <SidebarWrap
      key={selectedNodeId}
      comment={comment}
      sideBarTitle="Properties"
      handleCloseSidebar={handleCloseSidebar}
      handleSetComment={handleSetComment}
    >
      <ContinuousLoadingFields
        nickname={nickname}
        onNicknameChange={setNickname}
        onBlur={() => handleSubmit()}
        additionalDataInputs={additionalDataInputs}
        onDataFieldsChange={handleDataFieldsChange}
      />
    </SidebarWrap>
  );
};
